import { useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { Printer, X } from "lucide-react";

const TEMPLATES = [
  { id: "5160", label: 'Avery 5160 — 30 per sheet (2⅝ × 1")' },
  { id: "5163", label: 'Avery 5163 — 10 per sheet (4 × 2")' },
  { id: "5164", label: 'Avery 5164 — 6 per sheet (4 × 3⅓")' },
  { id: "5167", label: 'Avery 5167 — 80 per sheet (1¾ × ½")' },
];

export const AssetLabels = ({ assets, onClose, onChanged }) => {
  const [template, setTemplate] = useState("5160");
  const [selected, setSelected] = useState(new Set(assets.map((a) => a.id)));
  const [busy, setBusy] = useState(false);

  const toggle = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });

  const print = async () => {
    if (selected.size === 0) { toast.error("Select at least one asset"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/helpdesk/assets/labels",
        { ids: [...selected], template }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
      const w = window.open(url, "_blank");
      if (!w) {
        const a = document.createElement("a");
        a.href = url;
        a.download = `asset-labels-${template}.pdf`;
        a.click();
      }
      toast.success(`Label sheet ready — ${selected.size} label(s)`);
      onChanged();
    } catch (e) { toast.error(fmtErr(e)); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-neutral-900/40 z-50 flex items-center justify-center px-4" data-testid="asset-labels-modal">
      <div className="bg-white rounded-2xl border border-neutral-200 shadow-xl max-w-md w-full p-7 max-h-[88vh] flex flex-col">
        <div className="flex items-center justify-between mb-1">
          <h3 className="font-semibold font-heading text-lg flex items-center gap-2"><Printer size={18} /> Print asset labels</h3>
          <button data-testid="labels-close" onClick={onClose} className="p-1.5 hover:bg-neutral-100 rounded-md"><X size={16} /></button>
        </div>
        <p className="text-xs text-neutral-400 mb-4">Code-128 barcode labels. Assets without a tag get one auto-assigned (AST-XXXXXX).</p>
        <label className="text-xs font-semibold text-neutral-600">Label template</label>
        <select data-testid="labels-template-select" value={template} onChange={(e) => setTemplate(e.target.value)}
          className="mt-1 mb-4 bg-white border border-neutral-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-neutral-900 outline-none w-full">
          {TEMPLATES.map((t) => <option key={t.id} value={t.id}>{t.label}</option>)}
        </select>
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-neutral-600">{selected.size} of {assets.length} selected</p>
          <button data-testid="labels-select-all" onClick={() => setSelected(selected.size === assets.length ? new Set() : new Set(assets.map((a) => a.id)))}
            className="text-xs text-neutral-500 underline hover:text-neutral-800">{selected.size === assets.length ? "Deselect all" : "Select all"}</button>
        </div>
        <div className="flex-1 overflow-y-auto border border-neutral-200 rounded-lg divide-y divide-neutral-100 mb-4 min-h-[120px]">
          {assets.length === 0 && <p className="p-4 text-xs text-neutral-400">No assets yet.</p>}
          {assets.map((a) => (
            <label key={a.id} data-testid={`labels-asset-${a.id}`} className="flex items-center gap-3 px-3 py-2 text-sm cursor-pointer hover:bg-neutral-50">
              <input type="checkbox" checked={selected.has(a.id)} onChange={() => toggle(a.id)} />
              <span className="flex-1 font-medium truncate">{a.name}</span>
              <span className="text-xs text-neutral-400 font-mono">{a.tag || "no tag"}</span>
            </label>
          ))}
        </div>
        <button data-testid="labels-print-button" onClick={print} disabled={busy}
          className="w-full py-2.5 rounded-md text-white bg-neutral-900 hover:bg-neutral-800 text-sm font-medium disabled:opacity-50">
          {busy ? "Generating..." : "Generate label PDF"}
        </button>
      </div>
    </div>
  );
};
